import { OnboardingType, AuthUserType } from "../../../../Actions/Types"
import { Reducer, Action } from "redux"
import { AuthAction } from "../../../Auth"
import { APIAuthenticationAction } from "../../../API/AuthenticationReducer"
import {
    APIAppTemplateModel,
    APISampleAppModel
} from "../../../../Models/API/AppTemplateModel"
import { randID } from "../../../../Helpers/Functions/random"
import { TemplateInstantiation } from "../../../../Models/Client/TemplateInstantiation"

export type TemplateInstantiateState = TemplateInstantiation | null

interface StartOnboarding {
    type: OnboardingType.start
    payload: {
        template: APIAppTemplateModel
        orgUsername?: string // undefined if user isn't signed-up yet, or user is creating app without organization
    }
}

interface ClearAction {
    type: OnboardingType.clear
}

interface SetInstanceID {
    type: OnboardingType.setInstanceID
    payload: string
}

interface SetOnboardingJobID {
    type: OnboardingType.setJobID
    payload: string
}

interface AddAttempt {
    type: OnboardingType.addAttempt
}

interface ToggleDemo {
    type: OnboardingType.toggleDemo
}

interface SetLabeler extends Action<OnboardingType.setLabeler> {
    payload: boolean
}

interface SetSampleAppIdentifier
    extends Action<OnboardingType.setSampleAppIdentifier> {
    payload?: string
}

interface SetSampleAppData extends Action<OnboardingType.setSampleAppData> {
    meta: {
        sampleAppIdentifier: string
    }
    payload: APISampleAppModel
}

interface ResetAttempt extends Action<OnboardingType.resetAttempt> {}

export type TemplateInstantiateAction =
    | SetOnboardingJobID
    | ClearAction
    | SetInstanceID
    | StartOnboarding
    | AddAttempt
    | ToggleDemo
    | SetLabeler
    | ResetAttempt
    | SetSampleAppIdentifier
    | SetSampleAppData

const initialState: TemplateInstantiateState = null

export const TemplateInstantiateReducer: Reducer<
    TemplateInstantiateState,
    TemplateInstantiateAction | AuthAction | APIAuthenticationAction
> = (state = initialState, action): TemplateInstantiateState => {
    switch (action.type) {
        case OnboardingType.start:
            const id = randID()

            return {
                id,
                templateIdentifier: action.payload.template.name,
                templateTitle: action.payload.template.metadata.name,
                inputDataRenderId: `onboarding_input_${id}`,
                paramsRenderId: `onboarding_parameters_${id}`,
                jobAttempts: [undefined],
                demo: false,
                canoticCrowd: false,
                sampleApps: action.payload.template.metadata.sampleApps,
                sampleAppDataEntries: {},
                orgUsername: action.payload.orgUsername,
                aiWorkflow:
                    action.payload.template.metadata.ai_workflow ?? false,
                isAppParamsNull:
                    action.payload.template.appParamsSchema === null ||
                    action.payload.template.appParamsSchema.params === null
            }
        case OnboardingType.setInstanceID:
            return state === null
                ? null
                : {
                      ...state,
                      instanceID: action.payload
                  }
        case OnboardingType.setJobID:
            return state === null
                ? null
                : {
                      ...state,
                      jobAttempts: state.jobAttempts.map((job, i, arr) =>
                          i === arr.length - 1 ? action.payload : job
                      )
                  }
        case OnboardingType.addAttempt:
            return state === null
                ? null
                : {
                      ...state,
                      jobAttempts: state.jobAttempts.concat([undefined])
                  }
        case OnboardingType.toggleDemo:
            return state === null
                ? null
                : {
                      ...state,
                      demo: !state.demo
                  }
        case OnboardingType.setLabeler:
            return state === null
                ? null
                : {
                      ...state,
                      canoticCrowd: action.payload
                  }
        case OnboardingType.setSampleAppIdentifier:
            return state === null
                ? null
                : {
                      ...state,
                      selectedSampleAppIdentifier: action.payload
                  }
        case OnboardingType.resetAttempt:
            return state === null
                ? null
                : {
                      ...state,
                      instanceID: undefined
                  }
        case OnboardingType.setSampleAppData:
            return state === null
                ? null
                : {
                      ...state,
                      sampleAppDataEntries: {
                          ...state.sampleAppDataEntries,
                          [action.meta.sampleAppIdentifier]: action.payload
                      }
                  }
        case OnboardingType.clear:
        case AuthUserType.signedOut:
            return null
        default:
            return state
    }
}
